import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Target, Sparkles, Pencil, Crown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { MACRO_META } from './trackingConstants';
import { MacroOverridePanel } from './MacroOverridePanel';

const FIELDS = [
  { k: 'calories', fr: 'Calories', en: 'Calories', unit: 'kcal' },
  { k: 'protein', fr: 'Protéines', en: 'Protein', unit: 'g' },
  { k: 'carbs', fr: 'Glucides', en: 'Carbs', unit: 'g' },
  { k: 'fat', fr: 'Lipides', en: 'Fat', unit: 'g' },
];

const colorOf = (k) => (k === 'calories' ? MACRO_META.calories.color : MACRO_META[k]?.color || '#fff');

/** Dérive une répartition % (somme exacte = 100) à partir des grammes actuels,
 * utilisée seulement si aucun % n'a encore été sauvegardé (ex: objectifs venant
 * d'un calcul ou d'un recalcul poids, jamais encore édités en %). */
const pctFromGrams = (g) => {
  const cal = g.calories || 2000;
  const protein = Math.max(0, Math.min(100, Math.round(((g.protein || 0) * 4) / cal * 100)));
  const carbs = Math.max(0, Math.min(100, Math.round(((g.carbs || 0) * 4) / cal * 100)));
  const fat = Math.max(0, 100 - protein - carbs); // absorbe l'arrondi -> total exact 100
  return { protein, carbs, fat };
};

export const GoalsCard = ({ api, en, premium, goals, onUpgrade, onChanged }) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const g = goals || { calories: 2000, protein: 130, carbs: 220, fat: 65 };

  const overridePct = (() => {
    const hasPct = g.protein_pct != null && g.carbs_pct != null && g.fat_pct != null
      && Math.round(g.protein_pct + g.carbs_pct + g.fat_pct) === 100;
    return hasPct ? { protein: Math.round(g.protein_pct), carbs: Math.round(g.carbs_pct), fat: Math.round(g.fat_pct) } : pctFromGrams(g);
  })();

  const saveOverride = async (payload) => {
    setBusy(true);
    try {
      const res = await api('/api/tracking/goals/percent', { method: 'PUT', body: JSON.stringify(payload) });
      toast.success(en ? 'Custom targets saved' : 'Objectifs personnalisés enregistrés');
      if (res.low_calorie_alert) {
        toast.info(en ? 'Your coach was notified of this low target.' : 'Votre coach a été informé de cet objectif bas.');
      }
      setEditing(false);
      onChanged && onChanged();
    } catch (r) {
      const msg = await r?.json?.().then((j) => j.detail).catch(() => null);
      toast.error(msg || (en ? 'Save failed' : "Échec de l'enregistrement"));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-5" data-testid="goals-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="flex items-center gap-2 font-bold text-white"><Target className="w-4 h-4 text-primary" /> {en ? 'My targets' : 'Mes objectifs'}</h3>
        {premium && !editing && (
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/calculateur-calories?returnTo=tracking')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform"
              data-testid="new-calculation-btn">
              <Sparkles className="w-3.5 h-3.5" />
              {en ? 'New calculation' : 'Nouveau calcul'}
            </button>
            <button onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 border border-white/10 hover:border-primary/50 hover:text-white transition-colors"
              data-testid="edit-macros-btn">
              <Pencil className="w-3.5 h-3.5" /> {en ? 'Edit' : 'Modifier'}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {FIELDS.map((f) => (
          <div key={f.k} className="rounded-xl bg-zinc-800/50 border border-white/10 p-3" data-testid={`goal-${f.k}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: colorOf(f.k) }} />
              <span className="text-xs text-zinc-400">{en ? f.en : f.fr}</span>
            </div>
            <p className="text-xl font-black text-white tabular-nums">{Math.round(g[f.k])}<span className="text-xs text-zinc-500 font-normal ml-0.5">{f.unit}</span></p>
          </div>
        ))}
      </div>

      {/* Override ultra-pro : calories cibles + répartition % (glucides/protéines/lipides) */}
      <AnimatePresence>
        {editing && (
          <MacroOverridePanel
            initial={{ calories: g.calories, pct: overridePct }}
            onSave={saveOverride}
            onCancel={() => setEditing(false)}
            busy={busy}
            en={en}
            api={api}
          />
        )}
      </AnimatePresence>

      {/* CTA premium */}
      {!premium && (
        <button onClick={onUpgrade}
          className="w-full mt-4 rounded-xl border border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 to-primary/10 px-4 py-3 flex items-center justify-between gap-3 hover:border-yellow-500/50 transition-colors text-left"
          data-testid="goals-upgrade-cta">
          <div className="flex items-center gap-2.5">
            <Crown className="w-4 h-4 text-yellow-400 shrink-0" />
            <p className="text-sm text-zinc-200">{en ? 'Fine-tune your exact macro split & unlock unlimited AI with Suivi Pro' : 'Ajustez précisément vos % de macros & débloquez l\'IA illimitée avec Suivi Pro'}</p>
          </div>
          <span className="text-xs font-bold text-black bg-gradient-to-r from-yellow-400 to-primary px-3 py-1.5 rounded-full whitespace-nowrap">{en ? 'Unlock' : 'Débloquer'}</span>
        </button>
      )}
    </div>
  );
};

export default GoalsCard;
